/**
 * Contact Service - Complete contact management
 *
 * Provides CRUD operations, sync functionality, and utility functions
 * for managing client contacts in IndexedDB.
 * Data reading should be done via useLiveQuery from Dexie React hooks.
 */

import db from '@/db';
import { Contact } from '../types/contact';
import { API_ENDPOINTS } from '../../config/env';
import { getCurrentPerson } from './user_service';
import { fetchWithAuth, AuthExpiredError } from '../../lib/fetch-utils';
import { softSignOut } from '../../lib/auth';
import { OfflineError } from '@/lib/sync-errors';
import { isOnline } from '../../lib/connectivity';
import { isNil } from '@mes-amis/super-form';

import { createLogger, LogLevel } from '../../lib/logger';

const logger = createLogger('ContactService', LogLevel.DEBUG);
// ============================================================================
// Types & Interfaces
// ============================================================================

/**
 * Interface for contact sync callbacks
 */
interface SyncContactCallbacks {
  onStart?: () => void;
  onSuccess?: (contact: Contact) => void;
  onError?: (message: string) => void;
}

// ============================================================================
// CRUD Operations
// ============================================================================

/**
 * Get all contacts for a specific client
 * @param clientId The client ID to get contacts for
 * @returns Promise resolving to array of contacts
 */
export async function getContactsForClient(clientId: number): Promise<Contact[]> {
  return await db.contacts
    .where('customer_id')
    .equals(clientId)
    .sortBy('created_at');
}

/**
 * Get a specific contact by ID
 * @param contactId The contact ID
 * @returns Promise resolving to the contact or undefined
 */
export async function getContact(contactId: number): Promise<Contact | undefined> {
  return await db.contacts.get(contactId);
}

/**
 * Create or update a contact
 * @param contact The contact data
 * @returns Promise resolving to the contact ID
 */
export async function saveContact(contact: Contact): Promise<number> {
  return await db.contacts.put(contact) as number;
}

/**
 * Delete a contact
 * @param contactId The contact ID to delete
 */
export async function deleteContact(contactId: number): Promise<void> {
  await db.contacts.delete(contactId);
}

/**
 * Get emergency contacts for a client
 * @param clientId The client ID
 * @returns Promise resolving to array of emergency contacts
 */
export async function getEmergencyContacts(clientId: number): Promise<Contact[]> {
  const contacts = await getContactsForClient(clientId);
  return contacts.filter(contact => contact.emergency);
}

/**
 * Get the designated contact (proxy) for a client
 * @param clientId The client ID
 * @returns Promise resolving to the designated contact or undefined
 */
export async function getDesignatedContact(clientId: number): Promise<Contact | undefined> {
  const contacts = await getContactsForClient(clientId);
  return contacts.find(contact => contact.designated);
}

/**
 * Get the caregiver contact for a client
 * @param clientId The client ID
 * @returns Promise resolving to the caregiver contact or undefined
 */
export async function getCaregiverContact(clientId: number): Promise<Contact | undefined> {
  const contacts = await getContactsForClient(clientId);
  return contacts.find(contact => contact.caregiver);
}

// ============================================================================
// Sync Operations
// ============================================================================

/**
 * Syncs an offline contact to the server
 *
 * @param contact The contact to sync
 * @param callbacks Optional callbacks for different stages of the process
 * @returns Promise with the updated contact after sync
 */
export async function syncContact(
  contact: Contact,
  callbacks?: SyncContactCallbacks
): Promise<Contact | null> {
  try {
    if (callbacks?.onStart) callbacks.onStart();

    // Check if already synced - can skip entirely
    if (contact.synced) {
      if (callbacks?.onSuccess) callbacks.onSuccess(contact);
      return contact;
    }

    // Check if we're online
    if (!isOnline()) {
      throw new OfflineError('Cannot sync while offline');
    }

    // Get current person for organization scoping
    const currentPerson = await getCurrentPerson();
    if (!currentPerson) {
      throw new Error('No current person selected - cannot sync contact');
    }

    // Prepare the payload for the API
    const payload = {
      contact: {
        relationship: contact.relationship,
        note: contact.note,
        caregiver: contact.caregiver,
        designated: contact.designated,
        emergency: contact.emergency,
        legal_representative: contact.legal_representative,
        physician: contact.physician,
        person_attributes: contact.person ? {
          first_name: contact.person.first_name,
          last_name: contact.person.last_name,
          email_account_attributes: contact.person.email ? {
            email: contact.person.email
          } : undefined,
          phone_numbers_attributes: contact.person.phone_numbers?.map(phone => ({
            number: phone.number
          }))
        } : undefined,
        address_attributes: contact.address ? {
          address_line1: contact.address.address_line1,
          address_line2: contact.address.address_line2,
          city: contact.address.city,
          state: contact.address.state,
          zip: contact.address.zip,
          country: contact.address.county
        } : undefined
      }
    };

    // Determine if this is a create or update operation
    // Draft that has never been synced needs POST, otherwise PATCH
    const isNewDraft = Boolean(contact.local_id && !contact.synced);
    const method = isNewDraft ? 'POST' : 'PATCH';
    const url = isNewDraft
      ? `${API_ENDPOINTS.clients}/${contact.customer_id}/contacts?current_person_id=${currentPerson.id}`
      : `${API_ENDPOINTS.clients}/${contact.customer_id}/contacts/${contact.id}?current_person_id=${currentPerson.id}`;

    // Make the API call to sync the contact
    const response = await fetchWithAuth(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload),
      timeout: 20000 // 20 second timeout for contact sync
    }, () => {
      // Handle auth expiration — soft cleanup that preserves the local DB.
      // The user's unsynced data is still on disk; they can re-authenticate
      // (registerDeviceIfNeeded fires automatically) and finish syncing.
      softSignOut();
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as { message?: string };
      throw new Error(errorData.message ?? `Sync failed: ${response.status} ${response.statusText}`);
    }

    const responseData = await response.json() as Contact;

    // Update the contact with sync information
    const updatedContact: Contact = {
      ...responseData,
      id: responseData.id, // Update ID to server ID
      local_id: contact.local_id, // Preserve local_id for tracking
      synced: true,
      updated_at: new Date().toISOString(),
      customer_id: contact.customer_id,
      organization_id: contact.organization_id
    };

    // If we got a new ID from the server (for new drafts), delete old record and create new one
    if (responseData.id && contact.id !== responseData.id && isNewDraft) {
      await db.transaction('rw', db.contacts, async () => {
        // Delete the old record with UUID
        await db.contacts.delete(contact.id);
        // Add the new record with server ID
        await db.contacts.add(updatedContact);
      });
    } else {
      // Otherwise just update the existing record
      await db.contacts.put(updatedContact);
    }

    if (callbacks?.onSuccess) callbacks.onSuccess(updatedContact);
    return updatedContact;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error syncing contact';
    logger.error('Error syncing contact:', error);
    if (callbacks?.onError) callbacks.onError(errorMessage);
    throw error;
  }
}

export interface ContactSweepResult {
  /** Contacts uploaded by this sweep. */
  synced: number;
  /** Contacts this sweep attempted and could not upload (still pending). */
  failed: number;
}

// Coalesces overlapping contact sweeps into one, like the document sweep in
// the event listener: reconnect can fan out to several triggers at once and
// parallel sweeps would read the same unsynced snapshot and re-POST contacts.
// A caller that arrives while a sweep is running gets the running sweep's
// promise and flags one more pass so a contact created mid-sweep is picked up.
let syncContactsInFlight: Promise<ContactSweepResult> | null = null;
let contactSweepRerunRequested = false;

/**
 * Sync all offline contacts to the server.
 */
export function syncOfflineContacts(): Promise<ContactSweepResult> {
  if (syncContactsInFlight) {
    contactSweepRerunRequested = true;
    return syncContactsInFlight;
  }
  syncContactsInFlight = runContactSweep().finally(() => {
    syncContactsInFlight = null;
  });
  return syncContactsInFlight;
}

async function runContactSweep(): Promise<ContactSweepResult> {
  let synced = 0;
  let failed = 0;
  const attemptedIds = new Set<number | string>();
  try {
    do {
      contactSweepRerunRequested = false;

      // Get all contacts that haven't been synced
      const offlineContacts = await db.contacts
        .filter(contact => !contact.synced)
        .toArray();

      for (const contact of offlineContacts) {
        if (contact.id !== undefined) attemptedIds.add(contact.id);
        try {
          await syncContact(contact);
          synced++;
        } catch (error) {
          // An expired session fails every remaining contact the same way;
          // stop and let the caller show the sign-in-again path.
          if (error instanceof AuthExpiredError) throw error;
          logger.error(`Failed to sync contact ${contact.id}:`, error);
          // Continue with next contact
        }
      }

      // A contact that fails is still pending and is re-attempted by every
      // rerun pass, so count distinct contacts that are still pending rather
      // than failures per pass. Counted inside the loop so a rerun requested
      // while counting is honoured. Mirrors the document sweep.
      failed = attemptedIds.size === 0
        ? 0
        : await db.contacts.where('id').anyOf([...attemptedIds]).filter((c) => !c.synced).count();
    } while (contactSweepRerunRequested);

    return { synced, failed };
  } catch (error) {
    logger.error('Error syncing offline contacts:', error);
    throw error;
  }
}

/** Number of contacts created offline that still need to be pushed, for one organization. */
export async function countPendingContacts(organizationId: number): Promise<number> {
  return db.contacts
    .where('organization_id')
    .equals(organizationId)
    .filter((contact) => !contact.synced)
    .count();
}

/**
 * Get unsynchronized contacts count
 */
export async function getUnsyncedContactsCount(): Promise<number> {
  const offlineContacts = await db.contacts
    .filter(contact => !contact.synced)
    .toArray();

  return offlineContacts.length;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Extract all contact IDs referenced in a document's super_form data
 * Searches recursively through all form fields for contact IDs
 */
export function extractContactIds(superForm: Record<string, unknown>): (number | string)[] {
  const contactIds = new Set<number | string>();

  // UUID regex pattern (8-4-4-4-12 format)
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  function searchForContactIds(obj: unknown): void {
    if (isNil(obj)) return;

    if (Array.isArray(obj)) {
      // Check if this is an array of contact IDs
      obj.forEach(item => {
        if (typeof item === 'number') {
          contactIds.add(item);
        } else if (typeof item === 'string') {
          if (item.startsWith('contact-')) {
            contactIds.add(item);
          } else if (uuidPattern.test(item)) {
            contactIds.add(item);
          }
        } else {
          // Recursively search if item is an object or nested array
          searchForContactIds(item);
        }
      });
    } else if (typeof obj === 'object') {
      Object.entries(obj as Record<string, unknown>).forEach(([_key, value]) => {
        // Recursively search nested objects
        searchForContactIds(value);
      });
    }
  }

  searchForContactIds(superForm);
  return Array.from(contactIds);
}

/**
 * Check if any of the provided contact IDs are unsynced
 */
export async function hasUnsyncedContacts(contactIds: (number | string)[]): Promise<boolean> {
  if (contactIds.length === 0) return false;

  for (const contactId of contactIds) {
    const contact = await db.contacts.get(contactId);
    if (contact && !contact.synced) {
      return true;
    }
  }

  return false;
}

/**
 * Get all unsynced contacts referenced by contact IDs
 */
export async function getUnsyncedContactsByIds(contactIds: (number | string)[]) {
  if (contactIds.length === 0) return [];

  const contacts = await Promise.all(
    contactIds.map(id => db.contacts.get(id))
  );

  return contacts.filter(c => c && !c.synced) as Array<NonNullable<typeof contacts[0]>>;
}

/**
 * Replace old contact IDs (UUIDs) with new contact IDs (numbers) in super_form data
 * after contacts have been synced
 */
export async function replaceContactIdsInSuperForm(
  superForm: Record<string, unknown>,
  oldContactIds: (number | string)[]
): Promise<Record<string, unknown>> {
  // Build a map of old ID -> new ID for contacts that were synced
  const idMap = new Map<string | number, string | number>();

  // UUID regex pattern (8-4-4-4-12 format)
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  for (const oldId of oldContactIds) {
    // Only check UUID string IDs (these are the ones that changed after sync)
    if (typeof oldId === 'string') {
      let contact;

      // Check if it's a "contact-" prefixed ID
      if (oldId.startsWith('contact-')) {
        // Find by local_id
        const contacts = await db.contacts.toArray();
        contact = contacts.find(c => c.local_id === oldId);
      }
      // Check if it's a plain UUID
      else if (uuidPattern.test(oldId)) {
        // Find by id (which stores the plain UUID for drafts)
        const contacts = await db.contacts.toArray();
        contact = contacts.find(c => c.local_id?.endsWith(oldId));
      }

      if (contact && contact.id !== oldId && typeof contact.id === 'number') {
        // Contact was synced and now has a numeric server ID
        idMap.set(oldId, contact.id);
      }
    }
  }

  // If no IDs were mapped, return original
  if (idMap.size === 0) {
    return superForm;
  }

  // Recursively replace IDs in the super_form
  function replaceIds(obj: unknown): unknown {
    if (isNil(obj)) return obj;

    if (Array.isArray(obj)) {
      return obj.map(item => {
        // Check if this array item is a contact ID that needs replacing
        if (idMap.has(item as string | number)) {
          return idMap.get(item as string | number);
        }
        // Otherwise recursively process the item
        return replaceIds(item);
      });
    } else if (typeof obj === 'object') {
      const result: Record<string, unknown> = {};
      Object.entries(obj as Record<string, unknown>).forEach(([key, value]) => {
        // Check if this value is a contact ID that needs replacing
        if (idMap.has(value as string | number)) {
          result[key] = idMap.get(value as string | number);
        } else {
          result[key] = replaceIds(value);
        }
      });
      return result;
    }

    return obj;
  }

  return replaceIds(superForm) as Record<string, unknown>;
}
